import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { type TextFieldProps } from './types';
import { useTextField } from './useTextField';
import { useTextarea } from './useTextarea';

// ============================================================================
// Icons
// ============================================================================

// Clear/X icon
const ClearIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" fill="currentColor" stroke="none" />
    <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" />
  </svg>
);

// Error icon
export const ErrorIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" fill="#e11b1b" />
    <text
      x="12"
      y="16"
      textAnchor="middle"
      fill="white"
      fontSize="14"
      fontWeight="bold"
      fontFamily="Arial"
    >
      !
    </text>
  </svg>
);

// ============================================================================
// TextField Component - Filled variant with bottom border
// ============================================================================

const TextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  Omit<TextFieldProps, 'variant'>
>(
  (
    {
      label,
      placeholder,
      supportingText,
      errorMessage,
      error = false,
      disabled = false,
      readOnly = false,
      leadingIcon,
      trailingIcon,
      showClearButton = false,
      onClear,
      value: controlledValue,
      defaultValue = '',
      onChange,
      className = '',
      width = 210,
      fullWidth = false,
      type = 'text',
      id: providedId,
      onFocus,
      onBlur,
      labelColor: customLabelColor,
      multiline = false,
      rows = 4,
      maxLength,
      allowEmoji = false,
      autoTrimMaxLength,
      dataTestId,
      ...inputProps
    },
    ref
  ) => {
    const inputTestId =
      dataTestId ?? (typeof label === 'string' ? label : 'optional-label');
    const textareaTestId =
      dataTestId ?? (typeof label === 'string' ? label : 'textarea');
    const textField = useTextField({
      value: controlledValue,
      defaultValue,
      error,
      errorMessage,
      disabled,
      placeholder,
      id: providedId,
      customLabelColor,
      onChange: onChange as (e: React.ChangeEvent<HTMLInputElement>) => void,
      onClear,
      onFocus: onFocus as (e: React.FocusEvent<HTMLInputElement>) => void,
      onBlur: onBlur as (e: React.FocusEvent<HTMLInputElement>) => void,
    });

    const textarea = useTextarea({
      value: controlledValue,
      defaultValue,
      error,
      errorMessage,
      disabled,
      placeholder,
      id: providedId,
      customLabelColor,
      onChange: onChange as (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
      onFocus: onFocus as (e: React.FocusEvent<HTMLTextAreaElement>) => void,
      onBlur: onBlur as (e: React.FocusEvent<HTMLTextAreaElement>) => void,
    });

    const inputId = multiline ? textarea.textareaId : textField.inputId;
    const inputRef = multiline ? textarea.textareaRef : textField.inputRef;
    const value = multiline ? textarea.value : textField.value;
    const hasValue = multiline ? textarea.hasValue : textField.hasValue;
    const hasError = multiline ? textarea.hasError : textField.hasError;
    const isFloating = multiline ? textarea.isFloating : textField.isFloating;
    const isFocused = multiline ? textarea.isFocused : textField.isFocused;
    const labelColor = multiline ? textarea.labelColor : textField.labelColor;
    const handleChange = multiline
      ? textarea.handleChange
      : textField.handleChange;
    const handleFocus = multiline
      ? textarea.handleFocus
      : textField.handleFocus;
    const handleBlur = multiline ? textarea.handleBlur : textField.handleBlur;
    const handleContainerClick = multiline
      ? textarea.handleContainerClick
      : textField.handleContainerClick;
    const handleClear = textField.handleClear;

    useImperativeHandle(
      ref,
      () =>
        (multiline
          ? textarea.textareaRef.current
          : textField.inputRef.current) as
          | HTMLInputElement
          | HTMLTextAreaElement
    );

    // Phone number specific state
    const [countryCode] = useState('+48');
    const isPhoneField = !multiline && type === 'tel';
    const isAmountField = !multiline && type === 'amount';

    // Handle phone number input - only allow numbers
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // Only allow numbers
      const numericValue = inputValue.replace(/\D/g, '');
      if (numericValue !== inputValue) {
        // Create a synthetic event with only numeric value
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: numericValue },
        } as React.ChangeEvent<HTMLInputElement>;
        (handleChange as (ev: React.ChangeEvent<HTMLInputElement>) => void)(
          syntheticEvent
        );
      } else {
        (handleChange as (ev: React.ChangeEvent<HTMLInputElement>) => void)(e);
      }
    };

    // Handle amount/price input - only allow digits and one decimal (max 2 decimal places)
    // Comma is accepted as decimal separator (Polish locale) and treated identically to dot.
    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const filtered = inputValue.replace(/[^\d.,]/g, '').replace(',', '.');
      const parts = filtered.split('.');
      const validValue =
        parts.length > 2
          ? `${parts[0] ?? ''}.${parts.slice(1).join('').slice(0, 2)}`
          : parts.length === 2 && (parts[1]?.length ?? 0) > 2
            ? `${parts[0] ?? ''}.${(parts[1] ?? '').slice(0, 2)}`
            : filtered;
      if (validValue !== inputValue) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: validValue },
        } as React.ChangeEvent<HTMLInputElement>;
        (handleChange as (ev: React.ChangeEvent<HTMLInputElement>) => void)(
          syntheticEvent
        );
      } else {
        (handleChange as (ev: React.ChangeEvent<HTMLInputElement>) => void)(e);
      }
    };

    // Handle text input - block emojis unless allowEmoji is true
    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;

      // Trim leading whitespace and enforce maxLength if autoTrimMaxLength is set
      if (autoTrimMaxLength) {
        inputValue = inputValue.trimStart().slice(0, autoTrimMaxLength);
      }

      if (!allowEmoji) {
        const filteredValue = inputValue.replace(
          /\p{Extended_Pictographic}/gu,
          ''
        );
        if (filteredValue !== inputValue) {
          const syntheticEvent = {
            ...e,
            target: { ...e.target, value: filteredValue },
          } as React.ChangeEvent<HTMLInputElement>;
          (handleChange as (ev: React.ChangeEvent<HTMLInputElement>) => void)(
            syntheticEvent
          );
          return;
        }
      }

      // If value was modified, create synthetic event
      if (inputValue !== e.target.value) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: inputValue },
        } as React.ChangeEvent<HTMLInputElement>;
        (handleChange as (ev: React.ChangeEvent<HTMLInputElement>) => void)(
          syntheticEvent
        );
        return;
      }

      (handleChange as (ev: React.ChangeEvent<HTMLInputElement>) => void)(e);
    };

    // Handle textarea input - block emojis unless allowEmoji is true
    const handleTextareaChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      let inputValue = e.target.value;

      // Trim leading whitespace and enforce maxLength if autoTrimMaxLength is set
      if (autoTrimMaxLength) {
        inputValue = inputValue.trimStart().slice(0, autoTrimMaxLength);
      }

      if (!allowEmoji) {
        const filteredValue = inputValue.replace(
          /\p{Extended_Pictographic}/gu,
          ''
        );
        if (filteredValue !== inputValue) {
          const syntheticEvent = {
            ...e,
            target: { ...e.target, value: filteredValue },
          } as React.ChangeEvent<HTMLTextAreaElement>;
          (
            handleChange as (ev: React.ChangeEvent<HTMLTextAreaElement>) => void
          )(syntheticEvent);
          return;
        }
      }

      // If value was modified, create synthetic event
      if (inputValue !== e.target.value) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: inputValue },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        (handleChange as (ev: React.ChangeEvent<HTMLTextAreaElement>) => void)(
          syntheticEvent
        );
        return;
      }

      (handleChange as (ev: React.ChangeEvent<HTMLTextAreaElement>) => void)(e);
    };

    // Determine which trailing icon to show
    // Priority: custom trailingIcon > error icon > clear button
    const renderTrailingIcon = () => {
      if (trailingIcon) {
        return (
          <div
            className={`shrink-0 ml-2 mr-2 text-neutral-50 flex items-center ${disabled ? 'opacity-60' : ''}`}
          >
            <div
              className="rounded-full p-2 transition-colors duration-150 flex items-center justify-center"
              style={{
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  e.currentTarget.style.backgroundColor = '#474A4C14';
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {trailingIcon}
            </div>
          </div>
        );
      }
      if (hasError) {
        return (
          <div
            className={`shrink-0 ml-2 mr-2 flex items-center justify-center ${disabled ? 'opacity-60' : ''}`}
          >
            <div
              className="rounded-full p-2 transition-colors duration-150 flex items-center justify-center"
              style={{
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  e.currentTarget.style.backgroundColor = '#474A4C14';
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <ErrorIcon className="w-6 h-6" />
            </div>
          </div>
        );
      }
      if (showClearButton && hasValue && !disabled && !readOnly) {
        return (
          <button
            type="button"
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()}
            aria-label="Wyczyść pole"
            className="shrink-0 ml-2 text-neutral-50 transition-colors flex items-center rounded-full p-2 focus:outline-none focus:ring-2 focus:ring-primary-color-40"
            style={{
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#474A4C14';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <ClearIcon className="w-5 h-5" />
          </button>
        );
      }
      return null;
    };

    // Multiline (textarea) layout – same look as TextField with character count
    if (multiline) {
      return (
        <div
          className={`${fullWidth ? 'w-full' : ''} ${className}`}
          style={{
            width: fullWidth
              ? '100%'
              : typeof width === 'number'
                ? `${width}px`
                : width,
            fontFamily: 'Roboto, sans-serif',
          }}
        >
          <div
            className={`
              group relative flex flex-col rounded-lg ${disabled ? '!transition-none' : 'transition-colors duration-75'}
              ${readOnly ? 'cursor-default bg-[#F6F6F6]' : disabled ? 'cursor-not-allowed opacity-[1.0] bg-[#F6F6F6]' : 'cursor-text bg-[#E5E7E9] hover:bg-neutral-85'}
            `}
            style={{
              borderBottom: hasError
                ? '2px solid #e11b1b'
                : isFocused
                  ? '2px solid #2a4455'
                  : 'none',
              paddingLeft: 12,
              paddingRight: 12,
              paddingTop: 8,
              paddingBottom: 8,
            }}
            onClick={handleContainerClick}
          >
            {!isFocused && !hasError && (
              <div
                className="absolute bottom-0"
                style={{
                  left: 12,
                  right: 12,
                  height: 1,
                  backgroundColor: '#6a7278',
                }}
              />
            )}
            <div className="flex flex-row items-start min-h-0">
              <div className="relative flex flex-col flex-1 min-h-0 min-w-0">
                {label && (
                  <label
                    htmlFor={inputId}
                    className={`absolute left-0 pointer-events-none origin-top-left w-full h-5 ${disabled ? '!transition-none' : 'transition-colors duration-150'} ${readOnly || disabled ? 'bg-[#F6F6F6]' : 'bg-[#E5E7E9] group-hover:bg-neutral-85'}`}
                    style={{
                      color: labelColor,
                      top: isFloating ? 0 : 16,
                      fontSize: isFloating ? 12 : 16,
                      lineHeight: '16px',
                      transition: disabled
                        ? 'none'
                        : 'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out, background-color 75ms ease',
                      zIndex: 1,
                    }}
                  >
                    {label}
                  </label>
                )}
                <textarea
                  ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                  id={inputId}
                  data-testid={textareaTestId}
                  value={value}
                  onChange={handleTextareaChange}
                  onFocus={
                    handleFocus as (
                      e: React.FocusEvent<HTMLTextAreaElement>
                    ) => void
                  }
                  onBlur={
                    handleBlur as (
                      e: React.FocusEvent<HTMLTextAreaElement>
                    ) => void
                  }
                  placeholder={!label ? placeholder : ''}
                  disabled={disabled}
                  readOnly={readOnly}
                  maxLength={maxLength}
                  rows={rows}
                  className={`
                  w-full bg-transparent outline-none resize-none
                  text-base leading-6 placeholder:text-neutral-50 overflow-y-auto
                  ${readOnly ? 'cursor-default text-neutral-10' : disabled ? 'cursor-not-allowed text-neutral-60' : 'text-neutral-10'}
                `}
                  style={{
                    fontSize: 16,
                    lineHeight: '24px',
                    paddingTop: label ? 28 : 0,
                    backgroundColor: 'transparent',
                  }}
                  {...(inputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                />
              </div>
              {renderTrailingIcon()}
            </div>
          </div>
          {(maxLength != null || errorMessage || supportingText) && (
            <div className="mt-1 px-4 flex items-start">
              {(errorMessage || supportingText) && (
                <p
                  className={`flex-1 text-xs ${hasError ? 'text-error-40' : 'text-neutral-50'} ${disabled ? 'text-neutral-60' : ''}`}
                  style={{ fontSize: 12, lineHeight: '16px' }}
                >
                  {errorMessage ?? supportingText}
                </p>
              )}
              {maxLength != null && (
                <p
                  className="shrink-0 ml-auto text-right text-neutral-50"
                  style={{ fontSize: 12, lineHeight: '16px' }}
                  aria-live="polite"
                >
                  {value.length}/{maxLength}
                </p>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        className={`${fullWidth ? 'w-full' : ''} ${className}`}
        style={{
          width: fullWidth
            ? '100%'
            : typeof width === 'number'
              ? `${width}px`
              : width,
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        {/* Input Container */}
        <div
          className={`
          relative flex ${isPhoneField || isAmountField ? 'items-stretch' : 'items-center'} rounded-lg ${disabled ? '!transition-none' : 'transition-colors duration-75'}
          ${isPhoneField || isAmountField ? 'pr-1' : 'px-1'}
          ${readOnly ? 'cursor-default bg-[#F6F6F6]' : disabled ? 'cursor-not-allowed opacity-[1.0] bg-[#F6F6F6]' : 'cursor-text bg-[#E5E7E9] hover:bg-neutral-85'}
        `}
          style={{
            height: 56,
            borderBottom: hasError
              ? '2px solid #e11b1b'
              : isFocused
                ? '2px solid #2a4455'
                : 'none',
            paddingLeft: '12px',
            paddingRight: '12px',
          }}
          onClick={handleContainerClick}
        >
          {/* Straight bottom border for unfocused state */}
          {!isFocused && !hasError && (
            <div
              className="absolute bottom-0"
              style={{
                left: '6px',
                right: '6px',
                height: '1px',
                backgroundColor: '#6a7278',
              }}
            />
          )}
          {/* Phone / Amount Field Layout */}
          {isPhoneField || isAmountField ? (
            <>
              {/* Left section: Country code (phone) or Currency (amount) */}
              <div className="flex flex-col items-center justify-center !pr-[8px] py-2 border-r border-[#474A4C]">
                <p className="text-bodySmall pr-[4px] text-on-surface-variant text-center whitespace-nowrap">
                  {isAmountField ? 'Waluta' : 'Numer kierunkowy'}
                </p>
                <div className="flex items-start gap-1 min-[1060px]:gap-2 justify-center">
                  {isAmountField ? (
                    <span className="text-sm min-[1060px]:text-base text-neutral-10 whitespace-nowrap">
                      zł - PLN
                    </span>
                  ) : (
                    <>
                      <img
                        src="/polandFlag.svg"
                        alt="poland"
                        className="w-5 h-3 min-[1060px]:w-6 min-[1060px]:h-4 shrink-0"
                      />
                      <span className="text-sm min-[1060px]:text-base text-neutral-10 whitespace-nowrap">
                        {countryCode}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Input Area */}
              <div className="flex-1 relative flex flex-col justify-center px-2 py-2">
                {/* Floating Label */}
                {label && (
                  <label
                    htmlFor={inputId}
                    className="absolute left-2 pointer-events-none origin-top-left"
                    style={{
                      color: labelColor,
                      top: isFloating ? 2 : '50%',
                      transform: isFloating ? 'none' : 'translateY(-50%)',
                      fontSize: isFloating ? 12 : 16,
                      lineHeight: '16px',
                      transition: disabled
                        ? 'none'
                        : 'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out, transform 200ms ease-out',
                    }}
                  >
                    {label}
                  </label>
                )}

                {/* Input */}
                <input
                  ref={inputRef as React.RefObject<HTMLInputElement>}
                  data-testid={inputTestId}
                  id={inputId}
                  type={isAmountField ? 'text' : 'tel'}
                  value={value}
                  onChange={
                    isAmountField
                      ? (handleAmountChange as React.ChangeEventHandler<HTMLInputElement>)
                      : handlePhoneChange
                  }
                  onFocus={
                    handleFocus as React.FocusEventHandler<HTMLInputElement>
                  }
                  onBlur={
                    handleBlur as React.FocusEventHandler<HTMLInputElement>
                  }
                  placeholder={isFocused || !label ? placeholder : ''}
                  disabled={disabled}
                  readOnly={readOnly}
                  inputMode={isAmountField ? 'decimal' : 'numeric'}
                  pattern={isAmountField ? undefined : '[0-9]*'}
                  className={`
                  w-full bg-transparent outline-none
                  text-base leading-6
                  placeholder:text-neutral-50
                  ${readOnly ? 'cursor-default text-neutral-10' : disabled ? 'cursor-not-allowed text-neutral-60' : 'text-neutral-10'}
                `}
                  style={{
                    fontSize: 16,
                    lineHeight: '24px',
                    paddingTop: label ? 16 : 0,
                  }}
                  {...inputProps}
                />
              </div>

              {/* Trailing Icon */}
              {renderTrailingIcon()}
            </>
          ) : (
            <>
              {/* Leading Icon */}
              {leadingIcon && (
                <div
                  className={`shrink-0 mr-3 mb-[5px] text-neutral-50 flex items-center ${disabled ? 'opacity-60' : ''}`}
                >
                  <div
                    className="rounded-full p-2 transition-colors duration-150 flex items-center justify-center cursor-pointer"
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.backgroundColor = '#474A4C14';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {leadingIcon}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="flex-1 relative flex flex-col justify-center py-2">
                {/* Floating Label */}
                {label && (
                  <label
                    htmlFor={inputId}
                    className="absolute left-0 pointer-events-none origin-top-left"
                    style={{
                      color: labelColor,
                      top: isFloating ? 4 : 16,
                      fontSize: isFloating ? 12 : 16,
                      lineHeight: '16px',
                      transition: disabled
                        ? 'none'
                        : 'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                    }}
                  >
                    {label}
                  </label>
                )}

                {/* Input */}
                <input
                  ref={inputRef as React.RefObject<HTMLInputElement>}
                  data-testid={inputTestId}
                  id={inputId}
                  type={type}
                  value={value}
                  onChange={handleTextChange}
                  onFocus={
                    handleFocus as React.FocusEventHandler<HTMLInputElement>
                  }
                  onBlur={
                    handleBlur as React.FocusEventHandler<HTMLInputElement>
                  }
                  placeholder={isFocused || !label ? placeholder : ''}
                  disabled={disabled}
                  readOnly={readOnly}
                  maxLength={maxLength}
                  className={`
                  w-full bg-transparent outline-none
                  text-base leading-6
                  placeholder:text-neutral-50
                  ${readOnly ? 'cursor-default text-neutral-10' : disabled ? 'cursor-not-allowed text-neutral-60' : 'text-neutral-10'}
                `}
                  style={{
                    fontSize: 16,
                    lineHeight: '24px',
                    paddingTop: label ? 12 : 0,
                  }}
                  {...inputProps}
                />
              </div>

              {/* Trailing Icon */}
              {renderTrailingIcon()}
            </>
          )}
        </div>

        {/* Supporting Text / Error Message */}
        {(supportingText || errorMessage) && (
          <p
            className={`
            mt-1 px-4
            text-xs leading-4

            ${hasError ? 'text-error-40' : 'text-neutral-50'}
            ${disabled ? 'text-neutral-60' : ''}
          `}
            style={{ fontSize: 12, lineHeight: '16px' }}
          >
            {errorMessage || supportingText}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
